import clsx from 'clsx';
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { McpLogo } from '@/shared/ui';
import type { AgentMcpItem } from '../../model';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

type RowAction = 'none' | 'auth' | 'retry';

/**
 * What the user can do about a row:
 *  - built-in (platform) servers → nothing (always available, not user-managed)
 *  - connected / checking → nothing
 *  - no_auth has no credentials: install it if missing, else just retry (never "Reconnect")
 *  - never installed, or stored creds rejected (unauthorized) → re-authenticate (orange);
 *    for static_headers that's the credentials modal, for oauth the authorize popup
 *  - any other live failure (unreachable / timeout / not an MCP) → retry the check (red)
 */
const rowAction = (item: AgentMcpItem): RowAction => {
  if (item.builtIn) return 'none';
  if (item.status === 'connected' || item.status === 'checking') return 'none';
  // no_auth carries no creds, so a failing *installed* one can only be retried, not re-credited.
  if (item.authType === 'no_auth') return item.installed ? 'retry' : 'auth';
  if (item.status === 'not_installed' || item.errorType === 'unauthorized') return 'auth';
  return 'retry';
};

/**
 * The auth action's label — the verb signals the interaction that follows (same rule as the
 * marketplace buttons): OAuth opens the provider popup (Connect/Reconnect); static_headers opens
 * the credentials modal ("Edit headers", the marketplace's verb for the same action). Not-installed
 * no_auth/static both read "Install".
 */
const authLabel = (item: AgentMcpItem, t: TFunction<'admin'>): string => {
  if (item.authType === 'oauth') {
    return item.installed
      ? t('userMcpServers.chatMcpServers.reconnect')
      : t('userMcpServers.mcpMarketplace.connect');
  }
  return item.installed
    ? t('userMcpServers.mcpMarketplace.editHeaders')
    : t('userMcpServers.mcpMarketplace.install');
};

const StatusIcon = ({ item }: { item: AgentMcpItem }) => {
  if (item.status === 'checking') {
    return <Spinner className="size-3.5 text-foreground-subtle" />;
  }
  if (item.status === 'connected') {
    return <CheckCircle2 className="size-3.5 shrink-0 text-green-600" />;
  }
  return (
    <AlertCircle
      className={clsx(
        'size-3.5 shrink-0',
        rowAction(item) === 'auth' ? 'text-orange-500' : 'text-destructive',
      )}
    />
  );
};

const RowStatus = ({ item }: { item: AgentMcpItem }) => {
  const { t } = useTranslation('admin');

  const action = rowAction(item);
  if (item.status === 'connected') {
    return (
      <span className="text-[10px] font-medium text-green-600">
        {t('userMcpServers.chatMcpServers.connected')}
      </span>
    );
  }
  if (item.status === 'checking') {
    return (
      <span className="text-[10px] text-foreground-subtle">
        {t('userMcpServers.chatMcpServers.checking')}
      </span>
    );
  }
  // Built-in servers have no fix action, so surface a failing one as plain (red) status text.
  if (item.builtIn && item.status === 'error') {
    return (
      <span className="text-[10px] font-medium text-destructive">
        {t('userMcpServers.chatMcpServers.unavailable')}
      </span>
    );
  }
  if (action === 'none') return null;
  // Auth/Retry link fades in on row hover (opacity keeps the row width stable — no reflow).
  return (
    <span className="flex items-center gap-0.5 text-[10px] font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
      {action === 'auth' ? authLabel(item, t) : t('userMcpServers.chatMcpServers.retry')}
      <ExternalLink className="size-2.5" />
    </span>
  );
};

interface McpStatusRowProps {
  item: AgentMcpItem;
  onAuth: (item: AgentMcpItem) => void;
  onRetry: (item: AgentMcpItem) => void;
}

/** A single MCP row: status icon + name/description + a hover-revealed Auth/Retry action. */
export const McpStatusRow = ({ item, onAuth, onRetry }: McpStatusRowProps) => {
  const { t } = useTranslation('admin');

  const action = rowAction(item);
  const actionable = action !== 'none';
  const act = () => (action === 'auth' ? onAuth(item) : onRetry(item));
  return (
    <div
      className={clsx(
        'group flex items-center gap-2.5 rounded-md px-2 py-2',
        actionable && 'cursor-pointer hover:bg-surface',
      )}
      role={actionable ? 'button' : undefined}
      tabIndex={actionable ? 0 : undefined}
      onClick={actionable ? act : undefined}
      onKeyDown={
        actionable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                act();
              }
            }
          : undefined
      }
    >
      <StatusIcon item={item} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-medium leading-tight text-foreground">
            {item.name}
          </span>
          {item.builtIn && (
            <span className="shrink-0 rounded bg-surface px-1 py-px text-[9px] font-medium uppercase tracking-wide text-foreground-subtle">
              {t('userMcpServers.chatMcpServers.builtIn')}
            </span>
          )}
        </div>
        {item.description && (
          <div className="truncate text-[10px] leading-tight text-foreground-subtle">
            {item.description}
          </div>
        )}
      </div>
      <div className="shrink-0">
        <RowStatus item={item} />
      </div>
    </div>
  );
};

interface ChatMcpServersProps {
  items: AgentMcpItem[];
  /** Drives the warning dot on the submenu trigger. */
  needsAttention: boolean;
  /** Open the credentials modal (install or re-credit). */
  onAuth: (item: AgentMcpItem) => void;
  /** Re-run the live health check in place. */
  onRetry: (item: AgentMcpItem) => void;
}

/**
 * "Connectors" row inside the chat composer's + Add menu. Opens a flyout listing the agent's
 * MCP servers (built-in + catalog) with live, per-user connection status; failing catalog rows
 * expose an Auth/Retry action on hover. Rows are plain (not menu items) so acting on one doesn't
 * dismiss the menu — the parent decides whether to close it (Auth) or keep it open for an in-place
 * re-check (Retry).
 */
export const ChatMcpServers = ({ items, needsAttention, onAuth, onRetry }: ChatMcpServersProps) => {
  const { t } = useTranslation('admin');
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center gap-2.5">
        <McpLogo className="shrink-0" />
        <span className="flex-1">{t('userMcpServers.chatMcpServers.connectors')}</span>
        {needsAttention && <span className="mr-1 size-2 rounded-full bg-orange-500" aria-hidden />}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-60 overflow-hidden p-0">
        <div className="border-b border-border-lighter px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-subtle">
            {t('userMcpServers.chatMcpServers.connectors')}
          </span>
        </div>
        <div className="p-1">
          {items.map((item) => (
            <McpStatusRow key={item.mcpCatalogId} item={item} onAuth={onAuth} onRetry={onRetry} />
          ))}
        </div>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
